import { useState } from 'react';

export const useCustomAlert = () => {
  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'OK',
    cancelText: 'Cancel',
    showCancel: false,
    onConfirm: null,
    onClose: null
  });

  const showAlert = ({
    title = "Alert",
    message,
    type = "info",
    confirmText = "OK",
    cancelText = "Cancel",
    showCancel = false,
    onConfirm = null
  }) => {
    return new Promise((resolve) => {
      setAlertConfig({
        isOpen: true,
        title,
        message,
        type,
        confirmText,
        cancelText,
        showCancel,
        onConfirm: () => {
          closeAlert();
          if (onConfirm) onConfirm();
          resolve(true);
        },
        onClose: () => {
          closeAlert();
          resolve(false);
        }
      });
    });
  };

  const closeAlert = () => {
    setAlertConfig(prev => ({ ...prev, isOpen: false }));
  };

  // Enhanced convenience methods for different alert types with modern styling
  const showSuccess = (message, options = {}) => {
    return showAlert({
      title: "✨ Success",
      message,
      type: "success",
      confirmText: "Awesome!",
      ...options
    });
  };

  const showError = (message, options = {}) => {
    return showAlert({
      title: "⚠️ Error",
      message,
      type: "error",
      confirmText: "Got it",
      ...options
    });
  };

  const showWarning = (message, options = {}) => {
    return showAlert({
      title: "⚡ Warning",
      message,
      type: "warning",
      confirmText: "Understood",
      ...options
    });
  };

  const showInfo = (message, options = {}) => {
    return showAlert({
      title: "💡 Information",
      message,
      type: "info",
      confirmText: "Got it",
      ...options
    });
  };

  const showConfirm = (message, options = {}) => {
    return showAlert({
      title: "🤔 Confirm Action",
      message,
      type: "warning",
      showCancel: true,
      confirmText: "Yes, do it",
      cancelText: "No, cancel",
      ...options
    });
  };

  // Additional modern alert types
  const showDeleteConfirm = (itemName = "item") => {
    return showAlert({
      title: "🗑️ Delete Confirmation",
      message: `Are you sure you want to delete this ${itemName}? This action cannot be undone.`,
      type: "error",
      showCancel: true,
      confirmText: "Yes, delete",
      cancelText: "Keep it"
    });
  };

  const showSaveSuccess = (itemName = "data") => {
    return showAlert({
      title: "💾 Saved Successfully",
      message: `Your ${itemName} has been saved successfully!`,
      type: "success",
      confirmText: "Perfect!"
    });
  };

  const showLoadingError = () => {
    return showAlert({
      title: "🔄 Loading Failed",
      message: "We couldn't load the data. Please check your connection and try again.",
      type: "error",
      confirmText: "Retry"
    });
  };

  const showNetworkError = () => {
    return showAlert({
      title: "🌐 Connection Issue",
      message: "Unable to connect to the server. Please check your internet connection.",
      type: "error",
      confirmText: "Try again"
    });
  };

  const showValidationError = (field = "form") => {
    return showAlert({
      title: "📝 Validation Error",
      message: `Please check your ${field} and fill in all required fields correctly.`,
      type: "warning",
      confirmText: "Fix it"
    });
  };

  return {
    alertConfig,
    showAlert,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
    showDeleteConfirm,
    showSaveSuccess,
    showLoadingError,
    showNetworkError,
    showValidationError,
    closeAlert
  };
};